// Share & Print modal: unlisted link activation/deactivation, QR preview,
// optional 4-digit Family Passcode (enter or randomize — the Change PIN
// flow), Revoke Link, listen stats, and a printable reading certificate
// with the Audio QR + passcode embedded (Full-Color or Ink-Saver).
import { createElement, useEffect, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Dices, Printer, Unlink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api } from "../../lib/api";
import { printCertificate } from "../../lib/printCert";
import { CertificateSVG } from "../dashboard/MilestoneCertificate";

export const ShareModal = ({ book, onClose, onChanged }) => {
  const [enabled, setEnabled] = useState(false);
  const [pinOn, setPinOn] = useState(false);
  const [pin, setPin] = useState("");
  const [token, setToken] = useState(null);
  const [inkSaver, setInkSaver] = useState(false);
  const [expiry, setExpiry] = useState("never");
  const certRef = useRef(null);

  useEffect(() => {
    if (!book) return;
    setEnabled(!!book.share_enabled);
    setPinOn(!!book.share_pin);
    setPin(book.share_pin || "");
    setToken(book.share_token || null);
    setExpiry(book.share_expiry || "never");
  }, [book]);

  if (!book) return null;
  const url = token ? `${window.location.origin}/vault/shared/${token}` : "";

  const toggleEnabled = async (v) => {
    setEnabled(v);
    try {
      const { data } = await api.post(`/vault/${book.book_id}/share`, { enabled: v });
      setToken(data.token);
      onChanged?.();
      toast.success(v ? "Public audio link is live!" : "Link deactivated — QR codes stop working.");
    } catch { toast.error("Could not update the link."); setEnabled(!v); }
  };
  const savePin = async () => {
    try {
      await api.post(`/vault/${book.book_id}/share`,
        pinOn ? { enabled, pin } : { enabled, clear_pin: true });
      onChanged?.();
      toast.success(pinOn ? "Family Passcode saved!" : "Passcode removed.");
    } catch { toast.error("Could not save the passcode."); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied!"); }
    catch { toast.error("Copy failed — the link is shown above."); }
  };
  const revoke = async () => {
    await api.delete(`/vault/${book.book_id}/share`).catch(() => {});
    setToken(null); setEnabled(false); setPinOn(false); setPin("");
    setExpiry("never");
    onChanged?.();
    toast.success("Public link revoked — the QR code is now inactive.");
  };
  const changeExpiry = async (v) => {
    setExpiry(v);
    try {
      const { data } = await api.post(`/vault/${book.book_id}/share`, { enabled, expires: v });
      setToken(data.token);
      onChanged?.();
      toast.success(v === "never" ? "Link stays active until you revoke it." : "Link expiration updated!");
    } catch { toast.error("Could not update the expiration."); }
  };
  const printIt = () => {
    const node = certRef.current;
    if (!node) return;
    let qrMarkup = "", label = "", passcode = "";
    if (enabled && token) {
      qrMarkup = ReactDOMServer.renderToStaticMarkup(createElement(QRCodeSVG,
        { value: url, size: 150, level: "M", marginSize: 2 }));
      label = `\u{1F3A7} Scan to hear ${book.child_name || "our reader"}'s reading!`;
      passcode = pinOn && pin.length === 4 ? pin : "";
    }
    const ok = printCertificate({
      svgMarkup: new XMLSerializer().serializeToString(node),
      qrMarkup, label, passcode, inkSaver, title: book.title,
    });
    if (!ok) toast.error("Pop-up blocked — allow pop-ups to print.");
  };

  return (
    <Dialog open={!!book} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="share-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Share &amp; Print — {book.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold">Public audio link active</span>
            <Switch checked={enabled} onCheckedChange={toggleEnabled} data-testid="share-active-toggle" />
          </div>
          {enabled && token && (
            <>
              <div className="flex items-center justify-center rounded-xl bg-white p-3" data-testid="share-qr">
                <QRCodeSVG value={url} size={132} level="M" marginSize={2} />
              </div>
              <div className="flex gap-2">
                <input readOnly value={url} className="flex-1 min-w-0 rounded-full border border-border bg-muted px-3 py-1.5 text-xs" data-testid="share-url" />
                <Button size="sm" variant="outline" className="rounded-full gap-1 shrink-0" onClick={copy} data-testid="share-copy-btn">
                  <Copy className="h-4 w-4" aria-hidden="true" /> Copy
                </Button>
              </div>
            </>
          )}
          <div className="rounded-xl border border-border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold">Link Expiration</span>
              <select value={expiry} onChange={(e) => changeExpiry(e.target.value)}
                className="rounded-full border border-border bg-background px-3 py-1.5 text-sm font-bold"
                aria-label="Link expiration" data-testid="share-expiry-select">
                <option value="never">Never (Default)</option>
                <option value="30d">30 Days</option>
                <option value="90d">90 Days</option>
                <option value="1y">1 Year</option>
              </select>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold">Enable Family Passcode</span>
              <Switch checked={pinOn} onCheckedChange={setPinOn} data-testid="share-pin-toggle" />
            </div>
            {pinOn && (
              <div className="flex items-center gap-2">
                <input inputMode="numeric" maxLength={4} value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  className="w-24 rounded-full border border-border bg-background px-3 py-1.5 text-center font-extrabold tracking-[0.3em]"
                  placeholder="0000" aria-label="4 digit passcode" data-testid="share-pin-input" />
                <Button size="sm" variant="outline" className="rounded-full gap-1"
                  onClick={() => setPin(String(Math.floor(1000 + Math.random() * 9000)))}
                  data-testid="share-pin-random">
                  <Dices className="h-4 w-4" aria-hidden="true" /> Randomize
                </Button>
              </div>
            )}
            <Button size="sm" className="rounded-full" onClick={savePin}
              disabled={pinOn && pin.length !== 4} data-testid="share-pin-save">
              Save passcode
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-muted-foreground">Ink-Saver print (black &amp; white)</span>
            <Switch checked={inkSaver} onCheckedChange={setInkSaver} data-testid="share-ink-toggle" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full flex-1 gap-1" onClick={printIt} data-testid="share-print-cert">
              <Printer className="h-4 w-4" aria-hidden="true" /> Print certificate
            </Button>
            {token && (
              <Button size="sm" variant="destructive" className="rounded-full flex-1 gap-1" onClick={revoke} data-testid="share-revoke-btn">
                <Unlink className="h-4 w-4" aria-hidden="true" /> Revoke Link
              </Button>
            )}
          </div>
          {(book.listen_count > 0 || book.last_listened_at) && (
            <p className="text-xs text-muted-foreground" data-testid="share-listen-stats">
              Listens: {book.listen_count || 0}
              {book.last_listened_at ? ` · Last played ${new Date(book.last_listened_at).toLocaleString()}` : ""}
            </p>
          )}
        </div>
        <div className="absolute -left-[9999px] top-0" aria-hidden="true">
          <CertificateSVG svgRef={certRef} name={book.child_name}
            headline={`${book.child_name || "Our reader"} read "${book.title}"!`}
            milestone={`${book.grade_band || ""}${book.stars ? ` · ${book.stars}-star read` : ""}`} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
